<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  VISIT_DETAIL_STATUS_ACTIONS,
  VISIT_DETAIL_TEXT,
} from '../utils/visitDetailPresenter';

const props = defineProps({
  hero: { type: Object, required: true },
  canConfirm: { type: Boolean, default: false },
  canFinish: { type: Boolean, default: false },
});

const emit = defineEmits([
  'action',
  'confirm',
  'finish',
  'reschedule',
  'cancel',
]);

const fallbackActions = computed(() =>
  [
    props.canConfirm && {
      key: VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT,
      label: VISIT_DETAIL_TEXT.CONFIRM_VISIT,
      icon: 'i-lucide-circle-check',
    },
    props.canFinish && {
      key: VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT,
      label: VISIT_DETAIL_TEXT.FINISH_VISIT,
      icon: 'i-lucide-flag',
    },
    {
      key: VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT,
      label: VISIT_DETAIL_TEXT.RESCHEDULE_VISIT,
      icon: 'i-lucide-calendar-clock',
      variant: 'ghost',
    },
    {
      key: VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT,
      label: VISIT_DETAIL_TEXT.CANCEL_VISIT,
      icon: 'i-lucide-calendar-x-2',
      variant: 'ghost',
    },
  ].filter(Boolean)
);

const actions = computed(() =>
  Array.isArray(props.hero?.actions)
    ? props.hero.actions
    : fallbackActions.value
);

const emitAction = actionKey => {
  emit('action', actionKey);

  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.CONFIRM_VISIT) {
    emit('confirm');
  }
  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.FINISH_VISIT) {
    emit('finish');
  }
  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.RESCHEDULE_VISIT) {
    emit('reschedule');
  }
  if (actionKey === VISIT_DETAIL_STATUS_ACTIONS.CANCEL_VISIT) {
    emit('cancel');
  }
};
</script>

<template>
  <LaisCard density="tight">
    <div
      class="grid gap-4 rounded-lais-sm border px-4 py-4 sm:grid-cols-[48px_1fr_auto] sm:items-center"
      :class="hero.toneClass"
    >
      <span class="grid size-12 place-items-center rounded-full bg-white/80">
        <span class="size-6" :class="[hero.icon]" aria-hidden="true" />
      </span>
      <div class="min-w-0">
        <span
          class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold uppercase"
          :class="hero.badgeClass"
        >
          {{ hero.label }}
        </span>
        <h2 class="mt-2 text-[18px] font-semibold text-lais-text">
          {{ hero.title }}
        </h2>
        <p class="mt-1 text-[13px] leading-relaxed text-lais-text-body">
          {{ hero.description }}
        </p>
      </div>
      <div v-if="actions.length" class="grid gap-2 sm:min-w-[180px]">
        <LaisPrimaryButton
          v-for="item in actions"
          :key="item.key"
          :icon="item.icon"
          :variant="item.variant || 'primary'"
          :disabled="item.disabled"
          size="sm"
          @click="emitAction(item.key)"
        >
          {{ item.label }}
        </LaisPrimaryButton>
      </div>
    </div>
  </LaisCard>
</template>
